import axios, { AxiosResponse } from "axios";
import { environment } from "../../environment";
import { useLogin } from "../login/login";
import { IBrandMockup, ICrmEmailMessage, ICrmError, IEmailAttachment, IEmailDraft, IEmailTemplate, IEmailTemplateInput, IUnsentEmail } from "./types";

const crmUrl = `${environment.v2Url}/crm`;

export type CrmEmailComposeFunctions = {
    undo: (messageId: number) => Promise<IUnsentEmail | ICrmError>;
    sendNow: (messageId: number) => Promise<ICrmEmailMessage | ICrmError>;
    upload: (leadId: number, file: File, onProgress?: (fraction: number) => void) => Promise<IEmailAttachment | ICrmError>;
    mockups: (leadId: number) => Promise<IBrandMockup[] | ICrmError>;
    attachMockup: (leadId: number, mockupId: number) => Promise<IEmailAttachment | ICrmError>;
    attachmentUrl: (attachmentId: number) => Promise<{ url: string } | ICrmError>;
    messageFileUrl: (messageId: number, index: number) => Promise<{ url: string } | ICrmError>;
    templates: () => Promise<IEmailTemplate[] | ICrmError>;
    saveTemplate: (input: IEmailTemplateInput & { id?: number }) => Promise<IEmailTemplate | ICrmError>;
    deleteTemplate: (id: number) => Promise<true | ICrmError>;
    templateUsed: (id: number) => Promise<true | ICrmError>;
    getDraft: (leadId: number, threadId?: number | null) => Promise<{ draft: IEmailDraft | null; attachments: IEmailAttachment[] } | ICrmError>;
    saveDraft: (leadId: number, draft: Partial<IEmailDraft>) => Promise<IEmailDraft | ICrmError>;
    deleteDraft: (leadId: number, threadId?: number | null) => Promise<true | ICrmError>;
    aiDraft: (leadId: number, instruction: string, threadId?: number | null) => Promise<{ subject: string; body_doc: Record<string, unknown> } | ICrmError>;
};

// #region useCrmEmailCompose
/** The composer's server calls: undo/send-now, files, templates, drafts and AI drafts. Never throws. */
export const useCrmEmailCompose = (): [CrmEmailComposeFunctions] => {
    const [, authToken, loginFunctions] = useLogin();
    const headers = { 'X-Wallmates-Auth': authToken };

    const handle = (err: any): ICrmError => {
        const status = err?.response?.status ?? 0;
        if (status === 401) loginFunctions.logout();
        const raw = String(err?.response?.data?.message ?? '');
        return {
            ok: false,
            status,
            message: raw.replace(/^\[[^\]]+\](\[[^\]]+\])?:\s*/, '') || (status === 0 ? 'Could not reach Wallmates. Check your connection.' : 'Something went wrong.')
        } as ICrmError;
    };
    const call = async <T,>(fn: () => Promise<AxiosResponse<T>>): Promise<T | ICrmError> => {
        try { return (await fn()).data; } catch (err: any) { return handle(err); }
    };
    const threadQuery = (threadId?: number | null) => (threadId ? `?thread_id=${threadId}` : '');

    return [{
        undo: (id) => call(() => axios.post(`${crmUrl}/emails/${id}/undo`, {}, { headers })),
        sendNow: (id) => call(() => axios.post(`${crmUrl}/emails/${id}/send-now`, {}, { headers })),
        upload: (leadId, file, onProgress) => {
            const form = new FormData();
            form.append('file', file, file.name);
            return call(() => axios.post(`${crmUrl}/leads/${leadId}/email-attachments`, form, {
                headers,
                onUploadProgress: (e) => { if (onProgress && e.total) onProgress(e.loaded / e.total); }
            }));
        },
        mockups: (leadId) => call(() => axios.get(`${crmUrl}/leads/${leadId}/email-mockups`, { headers })),
        attachMockup: (leadId, mockupId) => call(() => axios.post(`${crmUrl}/leads/${leadId}/email-attachments/mockup`, { mockup_id: mockupId }, { headers })),
        attachmentUrl: (id) => call(() => axios.get(`${crmUrl}/email-attachments/${id}/url`, { headers })),
        messageFileUrl: (messageId, index) => call(() => axios.get(`${crmUrl}/emails/${messageId}/files/${index}/url`, { headers })),
        templates: () => call(() => axios.get(`${crmUrl}/email-templates`, { headers })),
        saveTemplate: ({ id, ...input }) => call(() => (id
            ? axios.put(`${crmUrl}/email-templates/${id}`, input, { headers })
            : axios.post(`${crmUrl}/email-templates`, input, { headers }))),
        deleteTemplate: (id) => call(() => axios.delete(`${crmUrl}/email-templates/${id}`, { headers })),
        templateUsed: (id) => call(() => axios.post(`${crmUrl}/email-templates/${id}/used`, {}, { headers })),
        getDraft: (leadId, threadId) => call(() => axios.get(`${crmUrl}/leads/${leadId}/email-draft${threadQuery(threadId)}`, { headers })),
        saveDraft: (leadId, draft) => call(() => axios.put(`${crmUrl}/leads/${leadId}/email-draft`, draft, { headers })),
        deleteDraft: (leadId, threadId) => call(() => axios.delete(`${crmUrl}/leads/${leadId}/email-draft${threadQuery(threadId)}`, { headers })),
        aiDraft: (leadId, instruction, threadId) => call(() => axios.post(`${crmUrl}/leads/${leadId}/email-ai-draft`, { instruction, thread_id: threadId ?? null }, { headers }))
    }];
};
